/* Shared pieces of a live-stream card, used by both places that draw one: the
 * `/live` grid (live.js) and the hero widget of the catalog (live-widget.js) —
 * and the one thing either of them reports back, a click on a card
 * (trackClick below).
 *
 * What lives here is everything that decides whether a value coming out of
 * `/api/v1/streams` may be put into the document at all. Those values are typed
 * by streamers and travel through a third-party API, so the origin allowlists
 * and the https rule below are the security boundary of the whole feature —
 * they are in one module precisely so there is one of them to audit and one to
 * fix, instead of two copies drifting apart.
 *
 * SECURITY: nothing here ever returns markup. A caller builds nodes with
 * createElement + textContent and asks this module for the values that go into
 * an `href` or a `src`; both come back as '' when they cannot be vouched for,
 * and '' is a card without a link or without a picture — never a broken one. */

(function (window, document) {
  'use strict';

  var RHF = window.RHF || (window.RHF = {});

  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* Twitch regenerates the preview frame of a live channel roughly every five
     minutes and serves it from a stable URL, which the browser then caches for
     far longer than that. Bucketing "now" into five-minute steps gives the same
     URL to every render inside a bucket — cache hit — and a fresh one once the
     CDN has a new frame to hand out. YouTube thumbnails already carry their own
     identity in the path and are left alone. */
  var THUMB_BUCKET_MS = 5 * 60 * 1000;

  /* Brand names and their public origins. The labels are written here rather
     than translated ten times, for the same reason the switcher's endonyms are:
     "Twitch" and "YouTube" are the same word in every one of the ten locales.
     The origin is the security half — a URL is only allowed into an href when
     it starts with the origin of the platform the row says it belongs to. */
  var PLATFORMS = {
    twitch: {
      label: 'Twitch',
      origin: 'https://www.twitch.tv/',
      viewBox: '0 0 24 24',
      path: 'M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z'
    },
    youtube: {
      label: 'YouTube',
      origin: 'https://www.youtube.com/',
      viewBox: '0 0 24 24',
      path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'
    }
  };

  /* Every label goes through t(); a stub keeps the page usable (keys instead of
     words) if the i18n runtime failed to load. It is read at call time, never
     cached, so a language switch re-translates everything — and so that a
     caller importing this module before i18n.js still gets the real runtime. */
  function t(key, params) {
    return RHF.i18n ? RHF.i18n.t(key, params) : String(key);
  }

  /** An image URL is only ever loaded over https, whatever the API sent. */
  function imageSrc(url) {
    return typeof url === 'string' && /^https:\/\//i.test(url) ? url : '';
  }

  /**
   * The public URL of a stream or a channel, or '' when it is not one.
   *
   * The API builds these itself, so this is belt and braces — but an href is
   * exactly the place where a value that travelled through a third-party API
   * must not be trusted, and "starts with the origin of its own platform" is a
   * rule the honest values all satisfy.
   */
  function platformUrl(url, platform) {
    var meta = PLATFORMS[platform];
    if (!meta || typeof url !== 'string') {
      return '';
    }
    return url.indexOf(meta.origin) === 0 ? url : '';
  }

  /** The preview frame of a broadcast, cache-bucketed on Twitch. */
  function thumbnailSrc(stream) {
    var url = imageSrc(stream.thumbnailUrl);
    if (!url || stream.platform !== 'twitch') {
      return url;
    }
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + 't=' + Math.floor(Date.now() / THUMB_BUCKET_MS);
  }

  /** The brand mark of a platform, as an inline <svg> of the given size. */
  function platformIcon(platform, size) {
    var meta = PLATFORMS[platform];
    if (!meta) {
      return null;
    }
    var px = String(size || 12);
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', meta.viewBox);
    svg.setAttribute('width', px);
    svg.setAttribute('height', px);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('d', meta.path);
    svg.appendChild(path);
    return svg;
  }

  /** "live for 2 h" — composed from the shared unit keys, like format.js does. */
  function uptimeLabel(stream, now) {
    var fmt = RHF.format;
    if (!fmt || !stream.startedAt) {
      return '';
    }
    var seconds = Math.floor(((now || Date.now()) - stream.startedAt.getTime()) / 1000);
    if (seconds < 0) {
      seconds = 0;
    }
    var value = seconds < fmt.HOUR
      ? t('unit.min', { n: Math.floor(seconds / fmt.MINUTE) })
      : t('unit.hour', { n: Math.floor(seconds / fmt.HOUR) });
    return t('live.uptime', { value: value });
  }

  /**
   * Tells the server that a reader opened a broadcast from one of our cards
   * (ARCHITECTURE §18): the curator's one question about the roster is which
   * channels people actually go and watch.
   *
   * What arrives is an anonymous daily number per channel and per place on
   * the site — `placement` is `live` (the `/live` grid) or `hero` (the
   * catalog's widget). The POST is not under `/api/v1`, whose contract is
   * GET-only, and it carries nothing of the reader: the route runs without a
   * session, and the server drops a repeat of the same click from the same
   * address for half an hour.
   *
   * Fire and forget, because the click it reports is a navigation to another
   * site and nothing here may slow it, block it or throw into it. A beacon
   * first: the browser keeps sending it after the page has gone, and it never
   * answers. Where there is none, or the browser refuses to queue one, a
   * keepalive fetch — the same survival, with a promise nobody waits on. The
   * body is form-encoded: that is what either transport makes of a
   * URLSearchParams, and what the server reads `placement` out of as it is.
   * A counter that failed is a number one short; it is never a link that did
   * not open.
   */
  function trackClick(id, placement) {
    try {
      var key = id === null || id === undefined ? '' : String(id);
      /* The id is interpolated into a path, so it has to be what the API
         sends: a row id. Anything else would be a request to some other URL. */
      if (!/^\d+$/.test(key)) {
        return;
      }
      var url = '/streams/' + key + '/click';
      var body = new window.URLSearchParams({ placement: String(placement) });
      var nav = window.navigator;

      if (nav && typeof nav.sendBeacon === 'function' && nav.sendBeacon(url, body)) {
        return;
      }
      if (typeof window.fetch === 'function') {
        window.fetch(url, {
          method: 'POST',
          body: body,
          keepalive: true,
          credentials: 'same-origin'
        }).catch(function () {
          /* nobody is waiting for the answer */
        });
      }
    } catch (err) {
      /* no URLSearchParams, a refused beacon that threw — the link still opens */
    }
  }

  /**
   * Reports every click on `link` that opens the broadcast: a primary click,
   * modifiers included (Ctrl/Cmd and Shift open the same page in a new tab or
   * window), and a middle click, which browsers dispatch as `auxclick` alone.
   * The right button is an `auxclick` too, and it opens a menu rather than
   * the stream — so that one is left out.
   *
   * `idOf` is asked at click time rather than once: the hero's card is one
   * static <a> repainted with whichever channel is selected. A card that lost
   * its href (a row without a public URL) goes nowhere and counts nothing.
   */
  function trackLink(link, idOf, placement) {
    if (!link || typeof link.addEventListener !== 'function') {
      return;
    }

    function report(event) {
      if (event.type === 'auxclick' && event.button !== 1) {
        return;
      }
      if (!link.hasAttribute('href')) {
        return;
      }
      trackClick(idOf(), placement);
    }

    link.addEventListener('click', report);
    link.addEventListener('auxclick', report);
  }

  RHF.streams = {
    PLATFORMS: PLATFORMS,
    imageSrc: imageSrc,
    platformUrl: platformUrl,
    thumbnailSrc: thumbnailSrc,
    platformIcon: platformIcon,
    uptimeLabel: uptimeLabel,
    trackClick: trackClick,
    trackLink: trackLink
  };
})(window, document);
